import { idb } from "../db/index.ts";
import { league, draft, trade } from "../core/index.ts";
import { g, lock, toUI, updatePlayMenu } from "../util/index.ts";
import { beforeLeague } from "../util/beforeView.ts";
import { PHASE, PLAYER } from "../../common/constants.ts";
import { defaultRealTeamInfo, defaultRealPlayerPhotos } from "../../common/defaultRealTeamInfo.ts";
import type { TradeTeams, RealTeamInfo, RealPlayerPhotos } from "../../common/types.ts";
import { applyRealPlayerPhotos } from "../core/league/processPlayerNewLeague.ts";
import { applyRealTeamInfo } from "../../common/applyRealTeamInfo.ts";
import originalTeamInfos from "../../common/teamInfos.ts";

export const applyMissingBranding = async () => {
	const branding = await getBranding();
	let photos = 0;
	let teamsChanged = 0;
	for (const p of await idb.merged.players.getAll()) {
		const previous = p.imgURL;
		applyRealPlayerPhotos(branding.realPlayerPhotos, p);
		if (p.imgURL !== previous) {
			await idb.cache.players.put(p);
			photos++;
		}
	}
	const teams = await idb.cache.teams.getAll();
	for (const t of teams) {
		if (!t.srID || !branding.realTeamInfo) {continue;}
		const original = originalTeamInfos[t.abbrev];
		const next = structuredClone(t);
		applyRealTeamInfo(next, branding.realTeamInfo, g.get("season"));
		const changes: Record<string, any> = {};
		for (const key of ["name", "region", "colors", "imgURL", "imgURLSmall"] as const) {
			const generic = key === "imgURL" || key === "imgURLSmall"
				? !t[key] || t[key]?.startsWith("/img/logos-")
				: original && JSON.stringify(t[key]) === JSON.stringify(original[key]);
			if (generic && JSON.stringify(next[key]) !== JSON.stringify(t[key])) {changes[key] = next[key];}
		}
		if (Object.keys(changes).length) {
			const currentSeason = await idb.cache.teamSeasons.indexGet("teamSeasonsByTidSeason", [t.tid, g.get("season")]);
			if (currentSeason) {
				for (const [key, value] of Object.entries(changes)) {
					if (JSON.stringify((currentSeason as any)[key]) === JSON.stringify((t as any)[key])) {(currentSeason as any)[key] = value;}
				}
				await idb.cache.teamSeasons.put(currentSeason);
			}
			Object.assign(t, changes);
			await idb.cache.teams.put(t);
			teamsChanged++;
		}
	}
	if (teamsChanged) {
		await league.setGameAttributes({ teamInfoCache: teams.map(({abbrev,disabled,imgURL,imgURLSmall,name,region})=>({abbrev,disabled,imgURL,imgURLSmall,name,region})) });
	}
	await idb.cache.flush();
	return { photos, teams: teamsChanged };
};

type ContractOffer = {
	exp: number;
	amount: number;
	smallestAmount?: boolean;
	disabledReason?: string;
};
let api: any;
export const registerApi = (value: any) => {
	api = value;
};

const reads = new Set(
	`advancedPlayerSearch checkParticipationAchievement countNegotiations dunkGetProjected exportDraftClass getExportFilename exportPlayerAveragesCsv exportPlayerGamesCsv generateFace getAutoPos getBornLoc getDefaultInjuries getDefaultNewLeagueSettings getDefaultTragicDeaths getDiamondInfo getJerseyNumberConflict getLeagueInfo getLeagueName getLeagues getNegotiationProps getNumPlayoffTeams getPlayerGraphStat getPlayersCommandPalette getLocal getPlayerBioInfoDefaults getPlayerRangeFooterStats getPlayerWatch getProjectedAttendance getRandomCollege getRandomCountry getRandomInjury getRandomJerseyNumber getRandomName getRandomRatings getRandomTeams getPlayersForScheduledEvents getSavedTrade getTeamGraphStat getTradingBlockOffers ping prefetchRealPlayerStats loadRetiredPlayers ovr ratingsStatsPopoverInfo runBefore validatePointsFormula validatePlayoffSettings`.split(
		" ",
	),
);
const teamActions = new Set(
	`autoProtect updateProtectedPlayers updateOptions updateKeyboardShortcuts setNote clearNotes updatePlayerWatch updatePlayersWatch clearWatchList setSavedTrade clearSavedTrades acceptContractNegotiation autoSortRoster cancelContractNegotiation clearTrade createTrade draftUser incrementTradeProposalsSeed proposeTrade reSignAll releasePlayer reorderRosterDrag resetPlayingTime retiredJerseyNumberDelete retiredJerseyNumberUpsert toggleColaOptOut tradeCounterOffer updateBudget updateKeepRosterSorted updatePlayThroughInjuries updatePlayingTime updateTrade`.split(
		" ",
	),
);
const adminActions = new Set(
	`deleteOldData reorderDraftDrag addTeam allStarDraftAll allStarDraftOne allStarDraftUser allStarDraftReset allStarDraftSetPlayers allStarGameNow contestSetPlayers draftLottery dunkSetControlling dunkSimNext dunkUser getPlayersForScheduledEvents handleUploadedDraftClass importPlayers importPlayersGetReal regenerateDraftClass regenerateSchedule expandVote relocateVote removePlayers setGOATFormula setScheduleFromEditor updateExpansionDraftSetup advanceToPlayerProtection autoProtect cancelExpansionDraft updateProtectedPlayers startExpansionDraft startFantasyDraft threeSimNext toggleTradeDeadline updateAwards updateConfsDivs updateGameAttributes updateGameAttributesGodMode updateLeague updatePlayoffTeams updateTeamInfo upsertCustomizedPlayer upsertScheduledEvent deleteScheduledEvent deleteScheduledEvents`.split(
		" ",
	),
);
// Official-account calls are not used by this installation.
const ignored = new Set([
	"initGold",
	"checkAccount",
	"initAds",
	"onLiveSimOver",
]);

let currentIdentity: string | undefined;
const undoOwners = new Map<number, string>();
const personalActions = new Set([
	"updateOptions",
	"updateKeyboardShortcuts",
	"setNote",
	"clearNotes",
	"updatePlayerWatch",
	"updatePlayersWatch",
	"clearWatchList",
	"setSavedTrade",
	"clearSavedTrades",
]);
const noteKeys = {
	players: "pid",
	games: "gid",
	draftPicks: "dpid",
	teamSeasons: "rid",
} as const;
export const capturePersonal = async () => {
	const watches = Object.fromEntries(
		(await idb.merged.players.getAll({ watch: true })).map((p) => [
			p.pid,
			p.watch,
		]),
	);
	const notes: Record<string, Record<string, string>> = {};
	for (const [store, key] of Object.entries(noteKeys)) {
		notes[store] = Object.fromEntries(
			(await (idb.merged as any)[store].getAll({ note: true })).map(
				(row: any) => [row[key], row.note],
			),
		);
	}
	return {
		watches,
		notes,
		savedTrades: await idb.cache.savedTrades.getAll(),
		options: await idb.meta.get("attributes", "options"),
		keyboardShortcuts: await idb.meta.get("attributes", "keyboardShortcuts"),
		realPlayerPhotos: await idb.meta.get("attributes", "realPlayerPhotos"),
		realTeamInfo: await idb.meta.get("attributes", "realTeamInfo"),
	};
};
const restorePersonal = async (prefs: any) => {
	for (const key of [
		"options",
		"keyboardShortcuts",
	] as const) {
		await idb.meta.put("attributes", prefs[key], key);
	}
	for (const p of await idb.merged.players.getAll({ watch: true })) {
		delete p.watch;
		await idb.cache.players.put(p);
	}
	for (const [pid, watch] of Object.entries(prefs.watches ?? {})) {
		const p = await idb.merged.players.get(Number(pid));
		if (p) {
			p.watch = Number(watch);
			await idb.cache.players.put(p);
		}
	}
	for (const store of Object.keys(noteKeys)) {
		for (const row of await (idb.merged as any)[store].getAll({ note: true })) {
			delete row.note;
			delete row.noteBool;
			await (idb.cache as any)[store].put(row);
		}
		for (const [id, note] of Object.entries(prefs.notes?.[store] ?? {})) {
			const row =
				(await (idb.cache as any)[store].get(Number(id))) ??
				(await idb.league.get(store as any, Number(id)));
			if (row) {
				row.note = String(note);
				row.noteBool = 1;
				await (idb.cache as any)[store].put(row);
			}
		}
	}
	for (const row of await idb.cache.savedTrades.getAll()) {
		await idb.cache.savedTrades.delete(row.hash);
	}
	for (const row of prefs.savedTrades ?? []) {
		await idb.cache.savedTrades.put(row);
	}
};

// Branding belongs to the shared league, rather than the last manager who
// happened to issue a command. The server also retains the owner's defaults.
export const getBranding = async () => ({
	realTeamInfo: await idb.meta.get("attributes", "realTeamInfo") as RealTeamInfo | undefined,
	realPlayerPhotos: await idb.meta.get("attributes", "realPlayerPhotos") as RealPlayerPhotos | undefined,
});
export const setBranding = async (branding: any) => {
	for (const key of ["realTeamInfo", "realPlayerPhotos"] as const) {
		await idb.meta.put("attributes", branding[key] ?? (key === "realTeamInfo" ? defaultRealTeamInfo : defaultRealPlayerPhotos), key);
	}
};

export const info = async () => ({
	lid: g.get("lid"),
	season: g.get("season"),
	phase: g.get("phase"),
	teams: (await idb.cache.teams.getAll())
		.filter((t) => !t.disabled)
		.map((t) => ({
			tid: t.tid,
			region: t.region,
			name: t.name,
			abbrev: t.abbrev,
		})),
});

export const dispatch = async (
	request: {
		tid: number;
		tids: number[];
		admin: boolean;
		type: string;
		name: string;
		param: any;
		draftTrade?: TradeTeams;
		acceptTrade?: TradeTeams;
		identity?: string;
		personal?: any;
	},
	conditions: any,
) => {
	const { tid, tids, admin, type, name } = request;
	let { param } = request;
	if (lock.get("gameSim") || lock.get("newPhase") || lock.get("drafting")) {
		throw new Error("A liga está sendo simulada. Aguarde e tente novamente.");
	}
	await league.setGameAttributes({
		userTid: tid,
		userTids: tids,
		gameOver: false,
	});
	if (request.identity && currentIdentity !== request.identity) {
		await restorePersonal(request.personal ?? {});
		currentIdentity = request.identity;
	}
	g.setWithoutSavingToDB("userTid", tid);
	if (request.draftTrade) {
		await idb.cache.trade.put({
			rid: 0,
			teams: structuredClone(request.draftTrade),
		});
	} else {
		await idb.cache.trade.put({
			rid: 0,
			teams: [
				{ tid, pids: [], dpids: [], pidsExcluded: [], dpidsExcluded: [] },
				{
					tid: tid === 0 ? 1 : 0,
					pids: [],
					dpids: [],
					pidsExcluded: [],
					dpidsExcluded: [],
				},
			],
		});
	}
	let result: any;
	const ownPlayer = async (pid: number) => {
		const p = await idb.cache.players.get(pid);
		if (!p || p.tid !== tid) {
			throw new Error("Você só pode modificar jogadores do seu time.");
		}
	};
	if (request.acceptTrade) {
		const teams = structuredClone(request.acceptTrade);
		if (teams[1].tid !== tid) {
			throw new Error("Esta proposta pertence a outro time.");
		}
		const clean = await trade.updatePlayers(structuredClone(teams));
		if (
			clean.some(
				(t, i) =>
					t.pids.length !== teams[i]!.pids.length ||
					t.dpids.length !== teams[i]!.dpids.length,
			)
		) {
			throw new Error(
				"A proposta ficou desatualizada. Envie uma nova proposta.",
			);
		}
		const summary = await trade.summary(clean);
		if (summary.warning) {
			throw new Error(String(summary.warning));
		}
		if (
			g.get("phase") >= PHASE.AFTER_TRADE_DEADLINE &&
			g.get("phase") <= PHASE.PLAYOFFS
		) {
			throw new Error("Trocas estão fechadas nesta fase.");
		}
		await idb.cache.trade.put({ rid: 0, teams: clean });
		result = await api.main.proposeTrade(true, conditions);
	} else if (type === "main" && name === "init") {
		// The server owns the worker environment and its database heartbeat.
		await api.main.init(
			{
				...param,
				enableLogging: false,
				useSharedWorker: true,
				heartbeatID: "multiplayer-server",
			},
			conditions,
		);
	} else if (type === "main" && name === "beforeView") {
		if (param.inLeague && param.lidUrl !== undefined && param.lidUrl !== g.get("lid")) {
			throw new Error("Abra as ligas pelo painel Multiplayer.");
		}
		// Global settings are outside /l/:lid, but must keep the shared league
		// open. The local-mode beforeNonLeague would close its database.
		if (param.inLeague) {
			await beforeLeague(g.get("lid"), conditions);
			await league.setGameAttributes({ userTid: tid, userTids: tids });
			g.setWithoutSavingToDB("userTid", tid);
			await updatePlayMenu();
		}
	} else if (type === "main" && ignored.has(name)) {
		result = undefined;
	} else {
		if (type === "main") {
			if (
				!reads.has(name) &&
				!teamActions.has(name) &&
				!(admin && adminActions.has(name))
			) {
				throw new Error(
					"Esta ação não está disponível para seu papel nesta liga multiplayer.",
				);
			}
			if (name === "runBefore") {
				if (
					!admin &&
					[
						"godMode",
						"dangerZone",
						"manageTeams",
						"manageConfs",
						"scheduleEditor",
						"scheduledEvents",
						"customizePlayer",
						"settings",
						"awardSettings",
						"newLeague",
						"deleteOldData",
						"importPlayers",
						"exportLeague",
					].includes(param.viewId)
				) {
					throw new Error("Esta tela é reservada ao administrador da liga.");
				}
				param = {
					...param,
					prevOutput: undefined,
					prevInputs: undefined,
					updateEvents: new Set(["firstRun", ...param.updateEvents]),
				};
			}
			if (name === "acceptContractNegotiation") {
				const props = await api.main.getNegotiationProps(param.pid);
				if (typeof props === "string") {
					throw new Error(props);
				}
				const option = props.contractOptions.find(
					(o: ContractOffer) =>
						o.exp === param.exp &&
						!o.disabledReason &&
						Math.abs(Math.round(o.amount * 1000) - param.amount) <= 1,
				);
				if (!option) {
					throw new Error(
						"Contrato inválido. Atualize a negociação e escolha uma das ofertas disponíveis.",
					);
				}
			}
			if (name === "reSignAll") {
				const negotiations = (await idb.cache.negotiations.getAll()).filter(
					(n) => n.tid === tid,
				);
				param = [];
				for (const n of negotiations) {
					const props = await api.main.getNegotiationProps(n.pid);
					if (typeof props === "string") {
						continue;
					}
					const offer =
						props.contractOptions.find(
							(o: ContractOffer) => !o.disabledReason && o.smallestAmount,
						) ??
						props.contractOptions.find((o: ContractOffer) => !o.disabledReason);
					if (offer) {
						param.push({
							pid: n.pid,
							contract: { exp: offer.exp },
							mood: {
								user: {
									willing: props.p.mood.user.willing,
									contractAmount: Math.round(offer.amount * 1000),
								},
							},
						});
					}
				}
			}
			if (name === "autoProtect" && param !== tid) {
				throw new Error("Time não autorizado.");
			}
			if (name === "updateProtectedPlayers") {
				if (param.tid !== tid) {
					throw new Error("Time não autorizado.");
				}
				for (const pid of param.protectedPids) {
					await ownPlayer(pid);
				}
			}
			if (name === "updatePlayingTime") {
				await ownPlayer(param.pid);
				if (
					!Number.isFinite(param.ptModifier) ||
					param.ptModifier < 0 ||
					param.ptModifier > 2
				) {
					throw new Error("Tempo de jogo inválido.");
				}
			}
			if (name === "reorderRosterDrag") {
				if (!Array.isArray(param) || new Set(param).size !== param.length) {
					throw new Error("Escalação inválida.");
				}
				for (const pid of param) {
					await ownPlayer(pid);
				}
			}
			if (name === "releasePlayer") {
				for (const pid of param.pids) {
					await ownPlayer(pid);
				}
			}
			if (name === "setSavedTrade" && param.tid !== tid) {
				throw new Error("Time não autorizado.");
			}
			if (name === "resetPlayingTime") {
				param = [tid];
			}
			if (name === "updateBudget") {
				for (const [key, value] of Object.entries(param.budgetLevels ?? {})) {
					if (
						!Number.isFinite(value) ||
						Number(value) < 0 ||
						Number(value) > (key === "ticketPrice" ? 10000 : 100)
					) {
						throw new Error("Orçamento inválido.");
					}
				}
			}
			if (name === "autoSortRoster") {
				param = { ...param, tids: [tid] };
			}
			if (name === "updatePlayThroughInjuries" && param.tid !== tid) {
				throw new Error("Time não autorizado.");
			}
			if (
				[
					"updateKeepRosterSorted",
					"retiredJerseyNumberUpsert",
					"retiredJerseyNumberDelete",
				].includes(name) &&
				param.tid !== undefined &&
				param.tid !== tid
			) {
				throw new Error("Time não autorizado.");
			}
			if (name === "draftUser") {
				const pick = (await draft.getOrder())[0];
				if (
					!(
						[
							PHASE.DRAFT,
							PHASE.FANTASY_DRAFT,
							PHASE.EXPANSION_DRAFT,
						] as number[]
					).includes(g.get("phase")) ||
					pick?.tid !== tid
				) {
					throw new Error("Aguarde a vez do seu time no draft.");
				}
				const p = await idb.cache.players.get(param);
				const expansion = g.get("expansionDraft");
				const available =
					p &&
					(g.get("phase") === PHASE.EXPANSION_DRAFT
						? expansion.phase === "draft" &&
							expansion.availablePids.includes(p.pid) &&
							(expansion.numPerTeam === undefined ||
								(expansion.numPerTeamDrafted[p.tid] ?? 0) <
									expansion.numPerTeam)
						: p.tid === PLAYER.UNDRAFTED &&
							(g.get("phase") === PHASE.FANTASY_DRAFT ||
								p.draft.year === g.get("season")));
				if (!available) {
					throw new Error("Jogador indisponível no draft.");
				}
			}
			if (
				["cancelContractNegotiation", "acceptContractNegotiation"].includes(
					name,
				)
			) {
				const pid = name === "cancelContractNegotiation" ? param : param.pid;
				const n = await idb.cache.negotiations.get(pid);
				if (n && n.tid !== tid) {
					throw new Error("Negociação pertence a outro time.");
				}
			}
			if (
				["createTrade", "updateTrade"].includes(name) &&
				(param?.[0]?.tid !== tid || param?.[1]?.tid === tid)
			) {
				throw new Error("A proposta deve partir do seu time.");
			}
			if (name === "proposeTrade") {
				param = false; // Neither a manager nor the commissioner may force another human's trade.
				const current = await trade.get();
				if (current.teams[0].tid !== tid) {
					throw new Error("Proposta não autorizada.");
				}
				if (tids.includes(current.teams[1].tid)) {
					const s = await trade.summary(current.teams);
					if (s.warning) {
						throw new Error(String(s.warning));
					}
					result = {
						accepted: false,
						message:
							"Proposta enviada ao outro participante. Aguardando aprovação.",
						multiplayerProposal: structuredClone(current.teams),
					};
				}
			}
			if (
				["updateGameAttributes", "updateGameAttributesGodMode"].includes(name)
			) {
				for (const key of [
					"userTid",
					"userTids",
					"lid",
					"spectator",
					"godMode",
				]) {
					if (key in param) {
						throw new Error(
							"Configuração controlada pelo servidor multiplayer.",
						);
					}
				}
			}
		} else if (type === "playMenu") {
			if (!admin) {
				throw new Error("Somente o administrador pode avançar a liga.");
			}
			if (
				(
					[PHASE.DRAFT, PHASE.FANTASY_DRAFT, PHASE.EXPANSION_DRAFT] as number[]
				).includes(g.get("phase")) &&
				["onePick", "untilYourNextPick", "untilEnd"].includes(name)
			) {
				const pick = (await draft.getOrder())[0];
				if (pick && tids.includes(pick.tid)) {
					throw new Error(
						"O draft aguarda a escolha do participante que controla este time.",
					);
				}
				if (name === "untilEnd") {
					throw new Error(
						"Use a próxima escolha para respeitar os times humanos.",
					);
				}
			}
		} else if (type === "actions") {
			if (
				["liveGame", "simGame", "simToGame", "untilPick"].includes(name) &&
				!admin
			) {
				throw new Error("Somente o administrador pode simular.");
			}
			if (name === "untilPick") {
				throw new Error(
					"Use avançar até a próxima escolha para respeitar os times humanos.",
				);
			}
			if (
				![
					"tradeFor",
					"addToTradingBlock",
					"untilPick",
					"liveGame",
					"simGame",
					"simToGame",
				].includes(name)
			) {
				throw new Error("Ação indisponível.");
			}
		} else if (type === "awardSettings") {
			if (!["getAwardCandidates", "getVariables"].includes(name)) {
				throw new Error("Ação indisponível.");
			}
		} else if (type === "undoLog") {
			if (
				!["undo", "remove"].includes(name) ||
				!request.identity ||
				undoOwners.get(param) !== request.identity
			) {
				throw new Error(
					"Esta ação de desfazer não pertence à sua conta ou expirou.",
				);
			}
			undoOwners.delete(param);
		} else if (type === "leagueFileUpload") {
			if (!admin || name !== "initialCheck") {
				throw new Error("Somente o administrador pode importar arquivos.");
			}
		} else if (type === "toolsMenu") {
			if (
				!admin ||
				[
					"resetDb",
					"autoPlaySeasons",
					"skipToAfterDraft",
					"skipToPreseason",
				].includes(name)
			) {
				throw new Error("Ferramenta indisponível no multiplayer.");
			}
		} else {
			throw new Error("Categoria indisponível no multiplayer.");
		}
		if (result === undefined) {
			const fn = (api as any)[type]?.[name];
			if (
				!Object.hasOwn((api as any)[type], name) ||
				typeof fn !== "function"
			) {
				throw new Error("Ação desconhecida.");
			}
			result = await fn(param, conditions);
		}
	}
	// Some original commands launch simulations without awaiting them.
	while (lock.get("gameSim") || lock.get("newPhase") || lock.get("drafting")) {
		await new Promise((resolve) => setTimeout(resolve, 50));
	}
	if (request.identity && !request.acceptTrade) {
		const key =
			typeof result === "number" && teamActions.has(name)
				? result
				: result?.undoKey;
		if (typeof key === "number") {
			undoOwners.set(key, request.identity);
		}
	}
	await idb.cache.flush();
	await toUI("updateLocal", [{ userTid: tid, userTids: tids }]);
	return {
		result,
		draftTrade: structuredClone((await trade.get()).teams),
		mutated: Boolean(
			request.acceptTrade ||
			(type !== "awardSettings" &&
				type !== "leagueFileUpload" &&
				(type !== "main" ||
					(!reads.has(name) &&
						!ignored.has(name) &&
						!personalActions.has(name) &&
						name !== "init" &&
						name !== "beforeView"))),
		),
		personal:
			personalActions.has(name) || type === "undoLog"
				? await capturePersonal()
				: undefined,
	};
};

export const bootstrap = (
	{ type, name, param }: { type: string; name: string; param: any },
	conditions: any,
) => {
	if (typeof g.get("lid") === "number") {
		throw new Error("Esta liga já foi criada.");
	}
	if (type === "main" && name === "beforeView") {
		return;
	}
	if (type === "main" && ignored.has(name)) {
		return;
	}
	if (type === "main" && name === "init") {
		param = {
			...param,
			enableLogging: false,
			useSharedWorker: true,
			heartbeatID: "multiplayer-server",
		};
	} else if (type === "main" && name === "runBefore") {
		if (
			!["newLeague", "defaultNewLeagueSettings", "globalSettings", "error"].includes(param.viewId)
		) {
			throw new Error("Conclua a criação da liga.");
		}
	} else if (
		!(type === "main" && (reads.has(name) || name === "createLeague" || name === "updateOptions")) &&
		!(type === "leagueFileUpload" && name === "initialCheck")
	) {
		throw new Error("Ação indisponível antes de criar a liga.");
	}
	if (name === "createLeague") {
		if (param.importLid !== undefined) {
			throw new Error("Envie um arquivo para importar a liga.");
		}
		param.settings = { ...param.settings, godMode: false };
	}
	return (api as any)[type][name](param, conditions);
};

export const describeTrade = (teams: TradeTeams) =>
	Promise.all(
		teams.map(async (t) => ({
			tid: t.tid,
			players: await Promise.all(
				t.pids.map(async (pid) => {
					const p = await idb.cache.players.get(pid);
					return {
						pid,
						name: p ? `${p.firstName} ${p.lastName}` : "Jogador indisponível",
						salary: p?.contract.amount,
						exp: p?.contract.exp,
					};
				}),
			),
			picks: await Promise.all(
				t.dpids.map(async (dpid) => {
					const p = await idb.cache.draftPicks.get(dpid);
					return {
						dpid,
						season: p?.season,
						round: p?.round,
						originalTid: p?.originalTid,
					};
				}),
			),
		})),
	);

export const exportStores = async (stores: string[]) => {
	await idb.cache.flush();
	const allowed = new Set<string>(Array.from(idb.league.objectStoreNames));
	const result: Record<string, any[]> = {};
	for (const name of stores) {
		if (!allowed.has(name)) {
			throw new Error("Tabela inválida.");
		}
		result[name] = await idb.league.getAll(name as any);
	}
	return result;
};
